// Tarjeta de Conexión con Google Workspace — solo Super Usuarios.
// Permite vincular la cuenta operativa central (Calendar + Drive) vía OAuth.
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Link2, Unplug, ExternalLink, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const ERROR_MESSAGES = {
  invalid_state: "El estado OAuth no es válido. Reintenta la conexión.",
  missing_code_or_state: "Google no devolvió los parámetros esperados.",
  token_exchange_failed: "Falló el intercambio de token. Verifica las credenciales.",
  no_refresh_token_revoke_and_retry:
    "Google no devolvió refresh_token. Ve a tu cuenta de Google → Seguridad → Permisos de terceros, revoca el acceso a esta app y reintenta.",
  wrong_account:
    "Te conectaste con una cuenta distinta a la esperada. Cierra sesión en esa cuenta antes de reintentar.",
};

export default function GoogleWorkspaceCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [driveCheck, setDriveCheck] = useState(null);   // v4.2.1 · resultado de la verificación real
  const [checking, setChecking] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const refresh = async () => {
    try {
      const { data } = await api.get("/google/status");
      setStatus(data);
    } catch (e) {
      // Si no es super-admin el backend responde 403; ocultamos la tarjeta.
      setStatus({ connected: false, denied: e.response?.status === 403 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // Mensajes desde el callback OAuth.
    if (searchParams.get("google_ok") === "1") {
      toast.success("¡Cuenta de Google Workspace conectada correctamente!");
      searchParams.delete("google_ok");
      setSearchParams(searchParams, { replace: true });
    }
    const err = searchParams.get("google_error");
    if (err) {
      const connected = searchParams.get("connected");
      const base = ERROR_MESSAGES[err] || `Error de Google: ${err}`;
      toast.error(connected ? `${base} (cuenta: ${connected})` : base);
      searchParams.delete("google_error");
      searchParams.delete("connected");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/google/oauth/start");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo iniciar OAuth");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("¿Desconectar Google Workspace? Las clases ya programadas seguirán existiendo en Google pero no podrás crear nuevas.")) return;
    try {
      await api.post("/google/disconnect");
      toast.success("Google Workspace desconectado.");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  if (loading) {
    return (
      <div className="jasq-card p-4 flex items-center gap-2 text-[#6E7591]">
        <Loader2 size={16} className="animate-spin" /> Verificando estado de Google…
      </div>
    );
  }
  if (status?.denied) return null;

  const connected = !!status?.connected;

  // v4.2.1 · «Conectado» solo significa que hay un token guardado. Esto lo verifica de verdad contra Drive.
  const verifyDrive = async () => {
    setChecking(true);
    try {
      const { data } = await api.get("/google/status", { params: { check: true } });
      setDriveCheck(data.drive || { ok: false, reason: "Sin respuesta" });
    } catch (e) {
      setDriveCheck({ ok: false, reason: formatApiErrorDetail(e.response?.data?.detail) || "No se pudo verificar" });
    } finally { setChecking(false); }
  };

  return (
    <div className="jasq-card p-5" data-testid="google-workspace-card">
      <div className="flex items-start gap-3">
        <span
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 font-extrabold text-white"
          style={{ background: "linear-gradient(135deg, #4285F4 0%, #34A853 50%, #FBBC04 100%)" }}
        >
          G
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="font-display font-extrabold text-[#1F2547]">Google Workspace · Calendar + Drive</p>
            <span
              className={`jasq-pill text-xs ${connected ? "bg-[#E7F4E9] text-[#2A7A36]" : "bg-[#FFE9F0] text-[#A52258]"}`}
              data-testid="google-status-pill"
            >
              {connected ? (
                <><CheckCircle2 size={11} /> Conectado</>
              ) : (
                <><AlertCircle size={11} /> Sin conectar</>
              )}
            </span>
          </div>
          <p className="text-xs text-[#6E7591] mt-1">
            Cuenta operativa central que genera los enlaces de Google Meet y organiza las grabaciones en Drive. Se conecta UNA vez vía OAuth.
          </p>
          <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-y-1 text-xs">
            <dt className="text-[#6E7591]">Cuenta esperada</dt>
            <dd className="font-mono text-[#1F2547] truncate">{status?.expected_email || "—"}</dd>
            <dt className="text-[#6E7591]">Cuenta conectada</dt>
            <dd className="font-mono text-[#1F2547] truncate">{status?.email || "—"}</dd>
            <dt className="text-[#6E7591]">Última actualización</dt>
            <dd className="font-mono text-[#1F2547]">{status?.updated_at ? new Date(status.updated_at).toLocaleString("es-ES") : "—"}</dd>
          </dl>

          {connected && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs" data-testid="google-drive-check">
              <button type="button" onClick={verifyDrive} disabled={checking} data-testid="google-verify-drive-btn"
                className="jasq-btn-ghost inline-flex items-center gap-2 text-sm disabled:opacity-60">
                {checking ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Verificar Drive
              </button>
              {driveCheck && (driveCheck.ok
                ? <span className="text-[#2A7A36] font-semibold" data-testid="google-drive-ok">
                    Drive operativo{driveCheck.storage_used_gb != null ? ` · ${driveCheck.storage_used_gb}${driveCheck.storage_limit_gb ? ` de ${driveCheck.storage_limit_gb}` : ""} GB usados` : ""}
                  </span>
                : <span className="text-[#A52258] font-semibold" data-testid="google-drive-fail">{driveCheck.reason}</span>)}
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {connected ? (
              <>
                <button
                  type="button"
                  onClick={handleDisconnect}
                  data-testid="google-disconnect-btn"
                  className="jasq-btn-ghost inline-flex items-center gap-2 text-sm"
                >
                  <Unplug size={14} /> Desconectar
                </button>
                <button
                  type="button"
                  onClick={handleConnect}
                  data-testid="google-reconnect-btn"
                  className="jasq-btn-ghost inline-flex items-center gap-2 text-sm"
                >
                  <Link2 size={14} /> Reconectar
                </button>
                <a
                  href="https://calendar.google.com"
                  target="_blank"
                  rel="noreferrer"
                  className="ml-auto text-xs text-[#A43790] font-bold inline-flex items-center gap-1 hover:underline"
                >
                  Abrir Calendar <ExternalLink size={11} />
                </a>
              </>
            ) : (
              <button
                type="button"
                onClick={handleConnect}
                disabled={connecting}
                data-testid="google-connect-btn"
                className="jasq-btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-60"
              >
                {connecting ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
                {connecting ? "Redirigiendo a Google…" : "Conectar con Google"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
